import {
  EffectivePaymentStatus,
  InvoiceType,
  StoredPaymentStatus,
  UpdateInvoicePaymentBody,
} from '../../models/invoice-v2.model';

/** Only tax invoices (305) use payment status and due-date tracking. */
export function isPaymentTrackingInvoiceType(type: number | null | undefined): boolean {
  return type != null && +type === InvoiceType.TAX_INVOICE;
}

/** Payment tracking is shown/edited inline only on confirmed tax invoices. */
export function shouldShowPaymentTrackingInUi(invoice: {
  confirm?: unknown;
  invoice_type?: unknown;
} | null | undefined): boolean {
  if (!invoice || !isPaymentTrackingInvoiceType(invoice.invoice_type as number)) {
    return false;
  }
  const confirm = invoice.confirm;
  return confirm === 1 || confirm === true || Number(confirm) === 1;
}

export function hasDueDate(value: unknown): boolean {
  return value != null && value !== '';
}

export function resolvePaymentFieldsForSubmit(
  invoiceType: number | null | undefined,
  formDueDate: unknown,
  formPaymentStatus: unknown,
): { due_date: string | null; payment_status: StoredPaymentStatus } {
  if (!isPaymentTrackingInvoiceType(invoiceType)) {
    return { due_date: null, payment_status: 'paid' };
  }
  const due_date = formatDueDateForApi(formDueDate);
  if (!due_date) {
    return { due_date: null, payment_status: 'paid' };
  }
  return {
    due_date,
    payment_status: formPaymentStatus === 'paid' ? 'paid' : 'unpaid',
  };
}

export interface InvoicePaymentLabelRow {
  payment_status?: unknown;
  due_date?: unknown;
  payment_status_label_ar?: string | null;
  payment_status_label_en?: string | null;
  payment_status_label_he?: string | null;
  effective_payment_status?: EffectivePaymentStatus;
}

/** Matches backend `DEFAULT_DUE_SOON_DAYS`. */
export const DEFAULT_DUE_SOON_DAYS = 7;

/** Normalize stored payment_status from API (only `paid` / `unpaid` are persisted). */
export function normalizeStoredPaymentStatus(value: unknown): StoredPaymentStatus {
  return value === 'paid' ? 'paid' : 'unpaid';
}

function toDateOnly(value: unknown): Date | null {
  const parsed = parseDueDateFromApi(value);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function todayDateOnly(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Mirror backend `computeEffectivePaymentStatus` for optimistic UI updates. */
export function computeEffectivePaymentStatus(
  paymentStatus: unknown,
  dueDate: unknown,
  dueSoonDays: number = DEFAULT_DUE_SOON_DAYS,
): EffectivePaymentStatus {
  if (normalizeStoredPaymentStatus(paymentStatus) === 'paid') {
    return 'paid';
  }

  const due = toDateOnly(dueDate);
  if (!due) {
    return 'paid';
  }

  const today = todayDateOnly();
  const dueSoonLimit = new Date(today);
  dueSoonLimit.setDate(dueSoonLimit.getDate() + dueSoonDays);

  if (due < today) {
    return 'overdue';
  }
  if (due <= dueSoonLimit) {
    return 'due_soon';
  }
  return 'unpaid';
}

function applyPaymentStatusLabelsToRow(
  row: Record<string, unknown>,
  effective: EffectivePaymentStatus,
): void {
  if (effective == null) {
    row['payment_status_label_ar'] = null;
    row['payment_status_label_en'] = null;
    row['payment_status_label_he'] = null;
    return;
  }
  row['payment_status_label_ar'] = getEffectivePaymentStatusFallbackLabel(effective, 'ar');
  row['payment_status_label_en'] = getEffectivePaymentStatusFallbackLabel(effective, 'en');
  row['payment_status_label_he'] = getEffectivePaymentStatusFallbackLabel(effective, 'he');
}

/** Localized label when API label fields are missing or were cleared after PATCH. */
export function getEffectivePaymentStatusFallbackLabel(
  effective: EffectivePaymentStatus,
  lang: string,
): string | null {
  if (effective == null) return null;
  const l = lang === 'ar' || lang === 'he' ? lang : 'en';
  const labels: Record<string, Record<'ar' | 'en' | 'he', string>> = {
    paid: { ar: 'مدفوعة', en: 'Paid', he: 'שולמה' },
    unpaid: { ar: 'غير مدفوعة', en: 'Unpaid', he: 'לא שולמה' },
    due_soon: { ar: 'قريبة الاستحقاق', en: 'Due soon', he: 'קרוב לפירעון' },
    overdue: { ar: 'متأخرة', en: 'Overdue', he: 'באיחור' },
  };
  return labels[effective]?.[l] ?? labels[effective]?.en ?? null;
}

/** Default due date suggestion in the calendar when marking unpaid (user must confirm). */
export function getDefaultUnpaidDueDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 30);
  return d;
}

/** Unpaid invoices need a due date — prompt the user instead of auto-saving one. */
export function needsDueDateBeforeUnpaid(
  invoice: { due_date?: unknown },
  status: StoredPaymentStatus,
): boolean {
  return status === 'unpaid' && !hasDueDate(invoice.due_date);
}

/** True when the stored status is already the selected value. */
export function shouldSkipPaymentStatusPatch(
  invoice: { payment_status?: unknown; due_date?: unknown },
  status: StoredPaymentStatus,
): boolean {
  return normalizeStoredPaymentStatus(invoice.payment_status) === status;
}

/** Build PATCH body when toggling paid/unpaid; due date is kept for history. */
export function buildPaymentStatusPatchBody(
  _invoice: { payment_status?: unknown; due_date?: unknown },
  status: StoredPaymentStatus,
): UpdateInvoicePaymentBody {
  return { payment_status: status };
}

/** PATCH when saving due date while switching to unpaid from the date picker. */
export function buildUnpaidWithDueDatePatchBody(dueDate: unknown): UpdateInvoicePaymentBody {
  return {
    payment_status: 'unpaid',
    due_date: formatDueDateForApi(dueDate),
  };
}

/** PATCH body when user clears due date — unpaid invoices revert to paid. */
export function buildClearDueDatePatchBody(invoice: {
  payment_status?: unknown;
}): UpdateInvoicePaymentBody {
  const body: UpdateInvoicePaymentBody = { due_date: null };
  if (normalizeStoredPaymentStatus(invoice.payment_status) === 'unpaid') {
    body.payment_status = 'paid';
  }
  return body;
}

/** API label fields — prefer backend labels, fall back to effective status text. */
export function getInvoicePaymentStatusLabel(
  invoice: InvoicePaymentLabelRow | null | undefined,
  lang: string,
): string | null {
  if (!invoice) return null;
  const effective = computeEffectivePaymentStatus(invoice.payment_status, invoice.due_date);
  if (effective == null) return null;

  if (lang === 'ar' && invoice.payment_status_label_ar) {
    return invoice.payment_status_label_ar;
  }
  if (lang === 'he' && invoice.payment_status_label_he) {
    return invoice.payment_status_label_he;
  }
  if (invoice.payment_status_label_en) {
    return invoice.payment_status_label_en;
  }
  if (invoice.payment_status_label_ar) {
    return invoice.payment_status_label_ar;
  }
  if (invoice.payment_status_label_he) {
    return invoice.payment_status_label_he;
  }
  return getEffectivePaymentStatusFallbackLabel(effective, lang);
}

/** Badge CSS class for `effective_payment_status`. */
export function getEffectivePaymentStatusBadgeClass(
  effective: EffectivePaymentStatus | undefined,
): string {
  switch (effective) {
    case 'paid':
      return 'payment-badge--paid';
    case 'unpaid':
      return 'payment-badge--unpaid';
    case 'due_soon':
      return 'payment-badge--due-soon';
    case 'overdue':
      return 'payment-badge--overdue';
    default:
      return '';
  }
}

export function parseDueDateFromApi(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.slice(0, 10) + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Merge PATCH payment response (or optimistic patch) into a list/view invoice row. */
export function applyPaymentFieldsToInvoiceRow(
  row: Record<string, unknown>,
  patch: { payment_status?: StoredPaymentStatus; due_date?: string | null },
  responseData?: Record<string, unknown> | null,
): void {
  const src = responseData ?? null;
  if (src?.['payment_status'] != null) {
    row['payment_status'] = src['payment_status'];
  } else if (patch.payment_status != null) {
    row['payment_status'] = patch.payment_status;
  }
  if (src && Object.prototype.hasOwnProperty.call(src, 'due_date')) {
    row['due_date'] = src['due_date'];
  } else if (Object.prototype.hasOwnProperty.call(patch, 'due_date')) {
    row['due_date'] = patch.due_date;
  }

  row['effective_payment_status'] = computeEffectivePaymentStatus(
    row['payment_status'],
    row['due_date'],
  );

  const responseHasLabels =
    !!src &&
    src['effective_payment_status'] != null &&
    (src['payment_status_label_ar'] != null ||
      src['payment_status_label_en'] != null ||
      src['payment_status_label_he'] != null);

  const paymentFieldsChanged =
    patch.payment_status != null ||
    Object.prototype.hasOwnProperty.call(patch, 'due_date') ||
    src?.['payment_status'] != null ||
    src?.['effective_payment_status'] != null ||
    src != null;

  if (responseHasLabels) {
    for (const key of [
      'payment_status_label_ar',
      'payment_status_label_en',
      'payment_status_label_he',
    ] as const) {
      if (src?.[key] != null) {
        row[key] = src[key];
      }
    }
  } else if (paymentFieldsChanged) {
    applyPaymentStatusLabelsToRow(row, row['effective_payment_status'] as EffectivePaymentStatus);
  }

  if (src?.['updatedDate'] != null) {
    row['updatedDate'] = src['updatedDate'];
  } else if (src?.['updated_at'] != null) {
    row['updatedDate'] = src['updated_at'];
  } else if (
    patch.payment_status != null ||
    Object.prototype.hasOwnProperty.call(patch, 'due_date')
  ) {
    row['updatedDate'] = new Date();
  }

  if (src?.['updated_by'] != null) {
    row['updated_by'] = src['updated_by'];
  }
}

export function formatDueDateForApi(date: unknown): string | null {
  if (date == null || date === '') return null;
  if (date instanceof Date) {
    if (isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    if (date.includes('/')) {
      const parts = date.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
  }
  return null;
}
